/**
 * Diagnostics landing page (#1873).
 *
 * Single hub for any diagnostic submission the rep needs to make.
 * Three sub-tools:
 *   /diagnostics/scanner — camera + capture problem barcodes
 *   /diagnostics/gps     — live GPS testing + submit a location sample
 *   /diagnostics/quick   — one-tap "send everything to support"
 *
 * Every submission from any of these always includes the FULL base
 * payload (device, app, network + bandwidth test, fresh GPS, recent
 * errors, storage state). See utils/diagnosticsCollector.ts.
 *
 * This page is reachable from Profile (More menu) → Diagnostics.
 * Reps go here when support asks them to. Not gated to admin.
 */

import { Link, useNavigate } from 'react-router-dom';

export default function Diagnostics() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="bg-white shadow-sm">
        <div className="flex items-center gap-4 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-slate-100"
            aria-label="Back"
          >
            <svg className="h-6 w-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-slate-800">Diagnostics</h1>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 text-sm text-blue-900">
          <p className="font-medium mb-1">Help support help you faster</p>
          <p>
            If support asks for diagnostics, pick the tool that matches the problem you're hitting.
            Every submission includes your device info, app version, network state, and current GPS so
            we can see exactly what's happening — no extra screenshots required.
          </p>
        </div>

        <Card
          to="/diagnostics/scanner"
          icon="📷"
          iconBg="bg-orange-100"
          iconColor="text-orange-700"
          title="Scanner / Barcode"
          subtitle="Camera won't read a tag, or reads the wrong value? Capture the problem tag and send it to support."
        />

        <Card
          to="/diagnostics/gps"
          icon="📍"
          iconBg="bg-emerald-100"
          iconColor="text-emerald-700"
          title="GPS / Location"
          subtitle="App thinks you're far from the store, or location won't update? Test GPS live and send a sample."
        />

        <Card
          to="/diagnostics/quick"
          icon="✉️"
          iconBg="bg-sky-100"
          iconColor="text-sky-700"
          title="Send Device Report"
          subtitle="Anything else — app crashing, slow, weird behavior. One tap sends the full device report."
        />

        <p className="pt-4 text-center text-xs text-slate-500">
          All submissions include the same base payload — device, app version, network, bandwidth test, current GPS, and recent errors.
        </p>
      </div>
    </div>
  );
}

function Card({
  to,
  icon,
  iconBg,
  iconColor,
  title,
  subtitle,
}: {
  to: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  title: string;
  subtitle: string;
}) {
  return (
    <Link
      to={to}
      className="flex w-full items-start gap-3 rounded-xl bg-white p-4 shadow-card transition-colors hover:bg-slate-50 active:scale-[0.99]"
    >
      <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${iconBg} ${iconColor} text-2xl`}>
        {icon}
      </div>
      <div className="flex-1 text-left">
        <div className="font-semibold text-slate-800">{title}</div>
        <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>
      </div>
      <svg className="h-5 w-5 flex-shrink-0 text-slate-400 mt-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}
